import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Alert, CircularProgress, Box,
} from "@mui/material";
import { financialAccountService } from "../../services/financialAccountService";
import { useAccounts } from "../../hooks/useAccounts";
import { AccountTransferCreate } from "../../types/accounts";

interface TransferDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** Pre-select this account as the source, e.g. when opened from an
     * account's own detail view. */
    defaultFromAccountId?: string;
}

const todayDateInput = () => new Date().toISOString().split("T")[0];

const defaultForm = (fromId?: string) => ({
    from_account_id: fromId || "",
    to_account_id: "",
    amount: "",
    fee: "",
    transfer_date: todayDateInput(),
    notes: "",
});

export const TransferDialog: React.FC<TransferDialogProps> = ({
    open, onClose, onSuccess, defaultFromAccountId,
}) => {
    const { accounts } = useAccounts();
    const [form, setForm] = useState(defaultForm(defaultFromAccountId));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setForm(defaultForm(defaultFromAccountId));
            setError("");
        }
    }, [open, defaultFromAccountId]);

    const fromAccount = accounts.find((a) => a.account_id === form.from_account_id);
    const toAccount = accounts.find((a) => a.account_id === form.to_account_id);

    // A LOAN account on either side changes what the "amount" actually
    // means — see AccountTransfer's docstring (app/models.py) for the
    // repayment/drawdown convention this mirrors.
    const amountLabel =
        toAccount?.type === "LOAN" ? "Repayment Amount (৳)"
        : fromAccount?.type === "LOAN" ? "Amount Borrowed (৳)"
        : "Amount (৳)";

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        const amount = parseFloat(form.amount);
        if (!form.from_account_id) { setError("Choose a source account"); return; }
        if (!form.to_account_id) { setError("Choose a destination account"); return; }
        if (form.from_account_id === form.to_account_id) { setError("Source and destination must be different"); return; }
        if (!amount || amount <= 0) { setError("Amount must be greater than 0"); return; }
        const fee = form.fee ? parseFloat(form.fee) : undefined;
        if (form.fee && (isNaN(fee!) || fee! < 0)) { setError("Fee must be a non-negative number"); return; }

        try {
            setSaving(true);
            const data: AccountTransferCreate = {
                from_account_id: form.from_account_id,
                to_account_id: form.to_account_id,
                amount,
                fee,
                transfer_date: `${form.transfer_date}T00:00:00`,
                notes: form.notes || undefined,
            };
            await financialAccountService.createTransfer(data);
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to record transfer");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Transfer Between Accounts</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <TextField
                            select label="From" value={form.from_account_id} fullWidth required
                            onChange={(e) => setForm((f) => ({ ...f, from_account_id: e.target.value }))}
                        >
                            {accounts.map((a) => (
                                <MenuItem key={a.account_id} value={a.account_id}>{a.name}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select label="To" value={form.to_account_id} fullWidth required
                            onChange={(e) => setForm((f) => ({ ...f, to_account_id: e.target.value }))}
                        >
                            {accounts.map((a) => (
                                <MenuItem key={a.account_id} value={a.account_id}>{a.name}</MenuItem>
                            ))}
                        </TextField>

                        {(fromAccount?.type === "LOAN" || toAccount?.type === "LOAN") && (
                            <Alert severity="info">
                                {toAccount?.type === "LOAN"
                                    ? `This reduces the balance owed on "${toAccount.name}".`
                                    : `This increases the balance owed on "${fromAccount?.name}".`}
                            </Alert>
                        )}

                        <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField
                                type="number" label={amountLabel} value={form.amount}
                                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                                fullWidth required inputProps={{ min: 0, step: 0.01 }}
                            />
                            <TextField
                                type="number" label="Fee (optional)" value={form.fee}
                                onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))}
                                fullWidth inputProps={{ min: 0, step: 0.01 }}
                                helperText="Deducted from the source account"
                            />
                        </Box>

                        <TextField
                            type="date" label="Transfer Date" value={form.transfer_date}
                            onChange={(e) => setForm((f) => ({ ...f, transfer_date: e.target.value }))}
                            fullWidth required InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            label="Notes (Optional)" value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            fullWidth multiline rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={22} /> : "Transfer"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
